import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, useParams, useLocation } from 'react-router-dom';
import SubscriptionManagement from './admin/SubscriptionManagement';
import ActivityLog from './admin/ActivityLog';
import TemplateManagement from './admin/TemplateManagement';
import { 
  Building2, Users, Settings, LogOut, LayoutDashboard, 
  Plus, Search, MoreHorizontal, CheckCircle, XCircle, Clock,
  ArrowLeft, Mail, Phone, Shield, UserPlus, Key, Trash2, Edit,
  TrendingUp, FileText, Activity, Globe, Palette, CreditCard, Image,
  History, Filter, RefreshCw, AlertTriangle, Eye, MessageSquare, Film,
  MousePointer, ListOrdered, HelpCircle, Quote, Layout
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '../utils/api';
import CMSEditor, { CMSSidebar } from './CMSEditor';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';

// Super Admin API helper
const adminApi = {
  get: (url) => api.get(url, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  post: (url, data) => api.post(url, data, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  put: (url, data) => api.put(url, data, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  delete: (url) => api.delete(url, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
};

// Current plans (active in the plans table, hidden ones included) for Super Admin plan assignment
const useActivePlans = () => {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    adminApi.get('/admin/plans').then((r) => setPlans(r.data || [])).catch(() => setPlans([]));
  }, []);
  return plans;
};

const PlanSelect = ({ value, onChange, plans, testId, className }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className={className} data-testid={testId}>
      <SelectValue placeholder="Select a plan" />
    </SelectTrigger>
    <SelectContent>
      {plans.map((p) => (
        <SelectItem key={p.slug} value={p.slug} data-testid={`${testId}-option-${p.slug}`}>
          {p.name}{p.is_public === false ? ' (hidden)' : ''}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// ==================== Login Component ====================
const SuperAdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/tenants/super-admin/login', { email, password });
      localStorage.setItem('super_admin_token', response.data.access_token);
      localStorage.setItem('super_admin', JSON.stringify(response.data.admin));
      onLogin(response.data.admin);
      toast.success('Welcome back, Super Admin!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-testid="super-admin-login">
      {/* Header - Shared component with logo from CMS */}
      <PublicHeader />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="border-slate-700 shadow-2xl bg-slate-800/50 backdrop-blur-sm">
            <CardHeader className="space-y-1 pb-4 text-center">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mb-4 border border-cyan-500/30">
                <Shield className="w-8 h-8 text-cyan-400" />
              </div>
              <CardTitle className="text-2xl font-bold text-white">Super Admin Login</CardTitle>
              <CardDescription className="text-slate-400">
                Access the platform control center
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter admin email"
                    required
                    className="dark-input h-11 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="dark-input h-11 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white font-semibold shadow-lg shadow-cyan-500/25" 
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In to Dashboard'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-400 mt-6">
            <Link to="/" className="text-cyan-400 hover:text-cyan-300 font-medium">
              ← Back to main site
            </Link>
          </p>
        </div>
      </main>

      {/* Footer - Shared component with logo from CMS */}
      <PublicFooter />
    </div>
  );
};

// ==================== Dashboard Overview ====================
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminApi.get('/tenants/stats/overview');
        setStats(response.data);
      } catch (error) {
        toast.error('Failed to fetch stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  const statCards = [
    { label: 'Total Tenants', value: stats?.stats?.total_tenants || 0, icon: Building2, color: 'bg-blue-500' },
    { label: 'Active Tenants', value: stats?.stats?.active_tenants || 0, icon: CheckCircle, color: 'bg-green-500' },
    { label: 'Trial Tenants', value: stats?.stats?.trial_tenants || 0, icon: Clock, color: 'bg-amber-500' },
    { label: 'Total Users', value: stats?.stats?.total_users || 0, icon: Users, color: 'bg-purple-500' },
    { label: 'Total Quotations', value: stats?.stats?.total_quotations || 0, icon: FileText, color: 'bg-indigo-500' },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500">Platform overview and statistics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, idx) => (
          <Card key={idx}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-3xl font-bold">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Tenants</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(stats?.recent_tenants || []).map((tenant) => (
              <div key={tenant.tenant_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium">{tenant.company_name}</p>
                    <p className="text-sm text-gray-500">/{tenant.slug}</p>
                  </div>
                </div>
                <Badge variant={tenant.status === 'ACTIVE' ? 'default' : 'secondary'}>
                  {tenant.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== Tenant List ====================
const TenantList = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTenant, setNewTenant] = useState({
    company_name: '',
    email: '',
    phone: '',
    subscription_plan: ''
  });
  const activePlans = useActivePlans();

  const fetchTenants = async () => {
    try {
      const response = await adminApi.get('/tenants/');
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreateTenant = async () => {
    try {
      await adminApi.post('/tenants/', newTenant);
      toast.success('Tenant created successfully');
      setShowCreateDialog(false);
      setNewTenant({ company_name: '', email: '', phone: '', subscription_plan: '' });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      ACTIVE: { className: 'bg-green-100 text-green-700', icon: CheckCircle },
      SUSPENDED: { className: 'bg-red-100 text-red-700', icon: XCircle },
      TRIAL: { className: 'bg-blue-100 text-blue-700', icon: Clock },
      PENDING: { className: 'bg-yellow-100 text-yellow-700', icon: Clock },
    }[status] || { className: 'bg-gray-100 text-gray-700', icon: Clock };
    
    return (
      <Badge className={config.className}>
        <config.icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const filteredTenants = tenants.filter(t => 
    t.company_name.toLowerCase().includes(search.toLowerCase()) ||
    t.slug.toLowerCase().includes(search.toLowerCase()) ||
    t.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="text-gray-500">Manage all registered organizations</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Tenant
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search tenants..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>URL Slug</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.map((tenant) => (
                  <TableRow key={tenant.tenant_id} className="cursor-pointer hover:bg-gray-50">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center">
                          {tenant.logo_url ? (
                            <img src={tenant.logo_url} alt="" className="w-8 h-8 rounded" />
                          ) : (
                            <Building2 className="w-5 h-5 text-purple-600" />
                          )}
                        </div>
                        <span>{tenant.company_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">/{tenant.slug}</code>
                    </TableCell>
                    <TableCell className="text-gray-500">{tenant.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{tenant.subscription_plan}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(tenant.status)}</TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/admin/tenants/${tenant.tenant_id}`)}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
            <DialogDescription>Add a new organization to the platform</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name *</Label>
              <Input
                value={newTenant.company_name}
                onChange={(e) => setNewTenant(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Acme Travel Agency"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newTenant.email}
                onChange={(e) => setNewTenant(prev => ({ ...prev, email: e.target.value }))}
                placeholder="admin@acme.com"
              />
            </div>
            <div>
              <Label>Phone / WhatsApp</Label>
              <Input
                value={newTenant.phone}
                onChange={(e) => setNewTenant(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+91 98765 43210"
              />
            </div>
            <div>
              <Label>Subscription Plan</Label>
              <PlanSelect
                value={newTenant.subscription_plan}
                onChange={(v) => setNewTenant(prev => ({ ...prev, subscription_plan: v }))}
                plans={activePlans}
                testId="new-tenant-plan-select"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTenant} className="bg-purple-600 hover:bg-purple-700">
              Create Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Tenant Detail with User Management ====================
const TenantDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditTenantDialog, setShowEditTenantDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  
  // Hard delete states - 3-step confirmation
  const [hardDeleteStep, setHardDeleteStep] = useState(0); // 0 = closed, 1-3 = warning steps
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null); // 'tenant' or 'user'
  const [hardDeleteUser, setHardDeleteUser] = useState(null);
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');
  
  // Onboarding email states
  const [showOnboardingEmailDialog, setShowOnboardingEmailDialog] = useState(false);
  const [onboardingEmailData, setOnboardingEmailData] = useState(null);
  const [onboardingEmailLoading, setOnboardingEmailLoading] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'AGENT'
  });
  const [tenantForm, setTenantForm] = useState({
    company_name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    status: 'ACTIVE',
    b2b_portal_enabled: false,
    b2b_auto_approve: false
  });
  const activePlans = useActivePlans();

  const fetchTenantData = async () => {
    try {
      const [usersResponse, detailsResponse] = await Promise.all([
        adminApi.get(`/tenants/${tenantId}/users`),
        adminApi.get(`/tenants/${tenantId}/full-details`)
      ]);
      setTenant(usersResponse.data.tenant);
      setUsers(usersResponse.data.users);
      setTenantDetails(detailsResponse.data);
      
      // Initialize form with tenant data
      const t = detailsResponse.data.tenant;
      setTenantForm({
        company_name: t.company_name || '',
        email: t.email || '',
        phone: t.phone || '',
        website: t.website || '',
        address: t.address || '',
        status: t.status || 'ACTIVE',
        b2b_portal_enabled: t.b2b_portal_enabled || false,
        b2b_auto_approve: t.b2b_auto_approve || false
      });
    } catch (error) {
      toast.error('Failed to fetch tenant data');
      navigate('/admin/tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantData();
  }, [tenantId]);

  // Fetch onboarding email data
  const fetchOnboardingEmail = async () => {
    setOnboardingEmailLoading(true);
    try {
      const response = await adminApi.get(`/tenants/${tenantId}/onboarding-email`);
      setOnboardingEmailData(response.data);
      setShowOnboardingEmailDialog(true);
    } catch (error) {
      toast.error('Failed to fetch onboarding email data');
    } finally {
      setOnboardingEmailLoading(false);
    }
  };

  // Generate onboarding email HTML
  const generateOnboardingEmailHTML = () => {
    if (!onboardingEmailData) return '';
    
    const { tenant, users, login_url_template } = onboardingEmailData;
    const baseUrl = window.location.origin;
    const loginUrl = `${baseUrl}${login_url_template}`;
    
    // Brand Colors
    const colors = {
      navy: '#0A1F44',
      cyan: '#00C8E8',
      white: '#FFFFFF'
    };
    
    // Platform branding URLs (from Super Admin CMS settings)
    const logoUrl = `${baseUrl}/api/uploads/branding/logo_172ed6df-2fb6-499e-8304-f2dfc973f300.svg`;
    const faviconUrl = `${baseUrl}/api/uploads/branding/logo_0b831ea6-2ba5-4bae-952a-db10630285b0.svg`;
    
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    
    const getRoleBadge = (role) => {
      const roleColors = {
        'AGENCY_ADMIN': colors.navy,
        'B2B_ADMIN': '#059669',
        'AGENT': colors.cyan,
        'VIEWER': '#6b7280'
      };
      const labels = {
        'AGENCY_ADMIN': 'Admin',
        'B2B_ADMIN': 'B2B Admin',
        'AGENT': 'Staff',
        'VIEWER': 'Viewer'
      };
      return `<span style="background: ${roleColors[role] || '#6b7280'}; color: ${colors.white}; padding: 2px 8px; border-radius: 4px; font-size: 11px;">${labels[role] || role}</span>`;
    };
    
    const usersRows = users.map(u => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid rgba(10,31,68,0.1); color: ${colors.navy};">${u.full_name || 'N/A'}</td>
        <td style="padding: 10px; border-bottom: 1px solid rgba(10,31,68,0.1); color: ${colors.navy};">${u.email}</td>
        <td style="padding: 10px; border-bottom: 1px solid rgba(10,31,68,0.1);">${getRoleBadge(u.role)}</td>
        <td style="padding: 10px; border-bottom: 1px solid rgba(10,31,68,0.1); font-family: 'Courier New', monospace; font-weight: bold; color: ${u.initial_password ? '#059669' : '#dc2626'}; background: ${u.initial_password ? '#f0fdf4' : '#fef2f2'};">${u.initial_password || '(Changed)'}</td>
      </tr>
    `).join('');

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 20px; font-family: 'Segoe UI', Arial, sans-serif; background: ${colors.white};">
  <div style="max-width: 600px; margin: 0 auto; background: ${colors.white}; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(10,31,68,0.1); border: 1px solid rgba(10,31,68,0.1);">
    
    <!-- Header -->
    <div style="background: ${colors.navy}; color: ${colors.white}; padding: 30px; text-align: center;">
      <img src="${logoUrl}" alt="OnTrOf" style="height: 50px; margin-bottom: 15px;" onerror="this.outerHTML='<div style=\\'margin-bottom:15px\\'><span style=\\'font-size:32px;font-weight:800;\\'>On</span><span style=\\'font-size:32px;font-weight:800;color:${colors.cyan}\\'>Tr</span><span style=\\'font-size:32px;font-weight:800;\\'>Of</span></div>'">
      <h1 style="margin: 0; font-size: 24px; font-weight: 600;">Welcome to OnTrOf!</h1>
      <p style="margin: 10px 0 0; opacity: 0.9; font-size: 14px;">Your Online Travel Office</p>
    </div>
    
    <!-- Organization Details -->
    <div style="padding: 25px;">
      <h2 style="color: ${colors.navy}; font-size: 18px; margin: 0 0 15px; padding-bottom: 10px; border-bottom: 3px solid ${colors.cyan};">Organization Details</h2>
      <table style="width: 100%; font-size: 14px;">
        <tr>
          <td style="padding: 8px 0; color: #6b7280; width: 140px;">Organization</td>
          <td style="padding: 8px 0; font-weight: 600; color: ${colors.navy};">${tenant.company_name}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #6b7280;">Login URL</td>
          <td style="padding: 8px 0;">
            <a href="${loginUrl}" style="color: ${colors.cyan}; font-weight: 600; text-decoration: none;">${loginUrl}</a>
          </td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #6b7280;">Subscription</td>
          <td style="padding: 8px 0;">
            <span style="background: rgba(0,200,232,0.15); color: ${colors.navy}; padding: 2px 10px; border-radius: 4px; font-weight: 600; border: 1px solid ${colors.cyan};">${tenant.subscription_plan}</span>
          </td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #6b7280;">Status</td>
          <td style="padding: 8px 0;">
            <span style="background: ${tenant.status === 'ACTIVE' ? '#d1fae5' : '#fee2e2'}; color: ${tenant.status === 'ACTIVE' ? '#059669' : '#dc2626'}; padding: 2px 10px; border-radius: 4px; font-weight: 600;">${tenant.status}</span>
          </td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #6b7280;">Created</td>
          <td style="padding: 8px 0; color: ${colors.navy};">${formatDate(tenant.created_at)}</td>
        </tr>
      </table>
    </div>
    
    <!-- User Accounts -->
    <div style="padding: 0 25px 25px;">
      <h2 style="color: ${colors.navy}; font-size: 18px; margin: 0 0 15px; padding-bottom: 10px; border-bottom: 3px solid ${colors.cyan};">User Accounts</h2>
      <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
        <thead>
          <tr style="background: rgba(10,31,68,0.05);">
            <th style="padding: 10px; text-align: left; color: ${colors.navy}; font-weight: 600;">Name</th>
            <th style="padding: 10px; text-align: left; color: ${colors.navy}; font-weight: 600;">Email</th>
            <th style="padding: 10px; text-align: left; color: ${colors.navy}; font-weight: 600;">Role</th>
            <th style="padding: 10px; text-align: left; color: ${colors.navy}; font-weight: 600;">Password</th>
          </tr>
        </thead>
        <tbody>
          ${usersRows}
        </tbody>
      </table>
      <p style="font-size: 12px; color: #9ca3af; margin: 15px 0 0; font-style: italic;">
        * Passwords shown as "(Changed)" have been modified by the user and cannot be displayed.
      </p>
    </div>
    
    <!-- Getting Started -->
    <div style="padding: 0 25px 25px;">
      <h2 style="color: ${colors.navy}; font-size: 18px; margin: 0 0 15px; padding-bottom: 10px; border-bottom: 3px solid ${colors.cyan};">Getting Started</h2>
      <ol style="margin: 0; padding-left: 20px; color: #374151; line-height: 1.8;">
        <li>Visit your login URL: <a href="${loginUrl}" style="color: ${colors.cyan}; font-weight: 600;">${loginUrl}</a></li>
        <li>Sign in with your email and password from the table above</li>
        <li>We recommend changing your password after first login (Settings → Change Password)</li>
        <li>Set up your agency branding and start creating quotations!</li>
      </ol>
    </div>
    
    <!-- Footer -->
    <div style="background: ${colors.navy}; padding: 20px 25px; text-align: center;">
      <div style="margin-bottom: 10px;">
        <img src="${faviconUrl}" alt="OnTrOf" style="height: 30px; vertical-align: middle;" onerror="this.style.display='none'">
      </div>
      <p style="margin: 0 0 5px; font-size: 12px; color: rgba(255,255,255,0.8);">Your Online Travel Office</p>
      <p style="margin: 10px 0 0; font-size: 13px; color: ${colors.white};">
        📧 support@ontrof.com | 📞 +91-8530412399
      </p>
      <p style="margin: 5px 0 0; font-size: 13px;">
        🌐 <a href="https://www.ontrof.com" style="color: ${colors.cyan};">www.ontrof.com</a>
      </p>
    </div>
    
  </div>
</body>
</html>`;
  };

  // Copy email HTML to clipboard
  const copyOnboardingEmail = async () => {
    const html = generateOnboardingEmailHTML();
    try {
      await navigator.clipboard.writeText(html);
      setEmailCopied(true);
      toast.success('Email HTML copied to clipboard!');
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCreateUser = async () => {
    try {
      await adminApi.post(`/tenants/${tenantId}/users`, userForm);
      toast.success('User created successfully');
      setShowUserDialog(false);
      setUserForm({ email: '', password: '', full_name: '', phone: '', role: 'AGENT' });
      fetchTenantData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await adminApi.put(`/tenants/${tenantId}/users/${user.user_id}`, {
        is_active: !user.is_active
      });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchTenantData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleResetPassword = async (user) => {
    try {
      const response = await adminApi.post(`/tenants/${tenantId}/users/${user.user_id}/reset-password`);
      toast.success(
        <div>
          <p>Password reset successful</p>
          <p className="font-mono text-sm mt-1">New password: {response.data.temporary_password}</p>
        </div>,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleResetAdminPassword = async () => {
    try {
      const response = await adminApi.post(`/tenants/${tenantId}/reset-admin-password`);
      toast.success(
        <div>
          <p>Admin password reset successful</p>
          <p className="text-sm mt-1">Email: {response.data.admin_email}</p>
          <p className="font-mono text-sm mt-1">New password: {response.data.temporary_password}</p>
        </div>,
        { duration: 15000 }
      );
      setShowResetPasswordDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset admin password');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await adminApi.delete(`/tenants/${tenantId}/users/${selectedUser.user_id}`);
      toast.success('User deleted');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchTenantData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleUpdateTenant = async () => {
    try {
      await adminApi.put(`/tenants/${tenantId}`, {
        company_name: tenantForm.company_name,
        email: tenantForm.email,
        phone: tenantForm.phone,
        website: tenantForm.website,
        address: tenantForm.address
      });
      toast.success('Tenant details updated');
      setShowEditTenantDialog(false);
      fetchTenantData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await adminApi.put(`/tenants/${tenantId}/status`, { status: newStatus });
      toast.success(`Tenant status updated to ${newStatus}`);
      fetchTenantData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateSubscription = async (planSlug) => {
    try {
      const res = await adminApi.put(`/tenants/${tenantId}/subscription`, { plan_slug: planSlug });
      toast.success(`${res.data.subscription_plan} plan assigned and active`);
      fetchTenantData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleUpdateB2BSettings = async () => {
    try {
      await adminApi.put(`/tenants/${tenantId}/b2b-settings`, {
        b2b_portal_enabled: tenantForm.b2b_portal_enabled,
        b2b_auto_approve: tenantForm.b2b_auto_approve
      });
      toast.success('B2B settings updated');
      fetchTenantData();
    } catch (error) {
      toast.error('Failed to update B2B settings');
    }
  };

  // Hard Delete Handlers
  const startHardDelete = (target, user = null) => {
    setHardDeleteTarget(target);
    setHardDeleteUser(user);
    setHardDeleteStep(1);
    setHardDeleteConfirmText('');
  };

  const cancelHardDelete = () => {
    setHardDeleteStep(0);
    setHardDeleteTarget(null);
    setHardDeleteUser(null);
    setHardDeleteConfirmText('');
  };

  const proceedHardDelete = () => {
    if (hardDeleteStep < 3) {
      setHardDeleteStep(hardDeleteStep + 1);
    }
  };

  const executeHardDelete = async () => {
    const confirmText = hardDeleteTarget === 'tenant' 
      ? tenant?.company_name 
      : hardDeleteUser?.full_name;
    
    if (hardDeleteConfirmText !== confirmText) {
      toast.error('Confirmation text does not match');
      return;
    }

    try {
      if (hardDeleteTarget === 'tenant') {
        const response = await adminApi.delete(`/tenants/${tenantId}/hard-delete`);
        toast.success(
          <div>
            <p className="font-semibold">Tenant Permanently Deleted</p>
            <p className="text-sm mt-1">Deleted: {response.data.deleted.quotations} quotations, {response.data.deleted.users} users</p>
          </div>,
          { duration: 8000 }
        );
        navigate('/admin/tenants');
      } else if (hardDeleteTarget === 'user' && hardDeleteUser) {
        await adminApi.delete(`/tenants/${tenantId}/users/${hardDeleteUser.user_id}/hard-delete`);
        toast.success(`User '${hardDeleteUser.full_name}' permanently deleted`);
        fetchTenantData();
      }
      cancelHardDelete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      AGENCY_ADMIN: { className: 'bg-purple-100 text-purple-700', label: 'Admin' },
      AGENT: { className: 'bg-blue-100 text-blue-700', label: 'Staff' },
      B2B_AGENT_ADMIN: { className: 'bg-green-100 text-green-700', label: 'B2B Admin' },
      B2B_AGENT_USER: { className: 'bg-gray-100 text-gray-700', label: 'B2B User' },
    }[role] || { className: 'bg-gray-100 text-gray-700', label: role };
    
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      ACTIVE: { className: 'bg-green-100 text-green-700', icon: CheckCircle },
      SUSPENDED: { className: 'bg-red-100 text-red-700', icon: XCircle },
      TRIAL: { className: 'bg-blue-100 text-blue-700', icon: Clock },
      PENDING: { className: 'bg-yellow-100 text-yellow-700', icon: Clock },
    }[status] || { className: 'bg-gray-100 text-gray-700', icon: Clock };
    
    return (
      <Badge className={config.className}>
        <config.icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tenants')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Tenant Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{tenant?.company_name}</h1>
                <p className="text-gray-500">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">/{tenantDetails?.tenant?.slug}</code>
                </p>
                <div className="flex items-center gap-2 mt-2">
                  {getStatusBadge(tenantDetails?.tenant?.status)}
                  <Badge variant="outline">{tenantDetails?.tenant?.subscription_plan}</Badge>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                onClick={fetchOnboardingEmail}
                disabled={onboardingEmailLoading}
              >
                <Mail className="w-4 h-4 mr-2" />
                {onboardingEmailLoading ? 'Loading...' : 'Onboarding Email'}
              </Button>
              <Button variant="outline" onClick={() => setShowResetPasswordDialog(true)}>
                <Key className="w-4 h-4 mr-2" />
                Reset Admin Password
              </Button>
              <Button variant="outline" onClick={() => setShowEditTenantDialog(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Tenant
              </Button>
              <Button 
                variant="outline" 
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => startHardDelete('tenant')}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Tenant
              </Button>
            </div>
          </div>
          
          {/* Quick Stats */}
          {tenantDetails?.stats && (
            <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{tenantDetails.stats.total_users}</p>
                <p className="text-sm text-gray-500">Total Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{tenantDetails.stats.active_users}</p>
                <p className="text-sm text-gray-500">Active Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{tenantDetails.stats.total_b2b_agents}</p>
                <p className="text-sm text-gray-500">B2B Agents</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-indigo-600">{tenantDetails.stats.total_quotations}</p>
                <p className="text-sm text-gray-500">Quotations</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <Button 
          variant={activeTab === 'users' ? 'default' : 'ghost'} 
          onClick={() => setActiveTab('users')}
        >
          <Users className="w-4 h-4 mr-2" /> Users
        </Button>
        <Button 
          variant={activeTab === 'settings' ? 'default' : 'ghost'} 
          onClick={() => setActiveTab('settings')}
        >
          <Settings className="w-4 h-4 mr-2" /> Settings & Rights
        </Button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Users</CardTitle>
              <CardDescription>{users.length} users in this organization</CardDescription>
            </div>
            <Button onClick={() => setShowUserDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {user.full_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{user.full_name}</p>
                          {user.phone && <p className="text-xs text-gray-500">{user.phone}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500">{user.email}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={user.is_active}
                          onCheckedChange={() => handleToggleUserStatus(user)}
                        />
                        <span className={user.is_active ? 'text-green-600' : 'text-gray-400'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleResetPassword(user)}
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={() => { setSelectedUser(user); setShowDeleteDialog(true); }}
                          title="Deactivate User"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => startHardDelete('user', user)}
                          title="Permanently Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="grid grid-cols-2 gap-6">
          {/* Status & Subscription */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status & Subscription</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-gray-500">Tenant Status</Label>
                <Select 
                  value={tenantDetails?.tenant?.status || 'ACTIVE'} 
                  onValueChange={handleUpdateStatus}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="SUSPENDED">Suspended</SelectItem>
                    <SelectItem value="TRIAL">Trial</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Subscription Plan</Label>
                <PlanSelect
                  value={tenantDetails?.tenant?.plan_slug}
                  onChange={handleUpdateSubscription}
                  plans={activePlans}
                  testId="tenant-plan-select"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1" data-testid="tenant-subscription-status">
                  Status: {tenantDetails?.tenant?.subscription_status || 'no_subscription'}
                </p>
              </div>
              {tenantDetails?.tenant?.subscription_expires && (
                <div>
                  <Label className="text-sm text-gray-500">Expires</Label>
                  <p className="text-sm mt-1">{new Date(tenantDetails.tenant.subscription_expires).toLocaleDateString()}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* B2B Portal Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">B2B Portal Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable B2B Portal</Label>
                  <p className="text-sm text-gray-500">Allow B2B agents to access this tenant</p>
                </div>
                <Switch
                  checked={tenantForm.b2b_portal_enabled}
                  onCheckedChange={(v) => setTenantForm(prev => ({ ...prev, b2b_portal_enabled: v }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Approve B2B Registrations</Label>
                  <p className="text-sm text-gray-500">Automatically approve new B2B agent signups</p>
                </div>
                <Switch
                  checked={tenantForm.b2b_auto_approve}
                  onCheckedChange={(v) => setTenantForm(prev => ({ ...prev, b2b_auto_approve: v }))}
                />
              </div>
              <Button onClick={handleUpdateB2BSettings} className="w-full mt-4">
                Save B2B Settings
              </Button>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{tenantDetails?.tenant?.email || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Phone / WhatsApp</p>
                    <p className="font-medium">{tenantDetails?.tenant?.phone || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Website</p>
                    <p className="font-medium">{tenantDetails?.tenant?.website || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium">{tenantDetails?.tenant?.address || '-'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user for {tenant?.company_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={userForm.full_name}
                onChange={(e) => setUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@company.com"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Min 8 characters"
              />
            </div>
            <div>
              <Label>Phone / WhatsApp</Label>
              <Input
                value={userForm.phone}
                onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+91 98765 43210"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select 
                value={userForm.role} 
                onValueChange={(v) => setUserForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AGENCY_ADMIN">Admin</SelectItem>
                  <SelectItem value="AGENT">Agent</SelectItem>
                  <SelectItem value="B2B_AGENT_ADMIN">B2B Admin</SelectItem>
                  <SelectItem value="B2B_AGENT_USER">B2B User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} className="bg-purple-600 hover:bg-purple-700">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={showEditTenantDialog} onOpenChange={setShowEditTenantDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tenant Details</DialogTitle>
            <DialogDescription>Update {tenant?.company_name}'s information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name</Label>
              <Input
                value={tenantForm.company_name}
                onChange={(e) => setTenantForm(prev => ({ ...prev, company_name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={tenantForm.email}
                onChange={(e) => setTenantForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Phone / WhatsApp</Label>
              <Input
                value={tenantForm.phone}
                onChange={(e) => setTenantForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div>
              <Label>Website</Label>
              <Input
                value={tenantForm.website}
                onChange={(e) => setTenantForm(prev => ({ ...prev, website: e.target.value }))}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={tenantForm.address}
                onChange={(e) => setTenantForm(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTenantDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateTenant} className="bg-purple-600 hover:bg-purple-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Admin Password Confirmation */}
      <AlertDialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Admin Password</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset the primary admin's password for {tenant?.company_name}. 
              A new temporary password will be generated and displayed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetAdminPassword} className="bg-purple-600 hover:bg-purple-700">
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate {selectedUser?.full_name}? This will disable their login access but preserve their data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-orange-600 hover:bg-orange-700">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 3-Step Hard Delete Confirmation Dialog */}
      <Dialog open={hardDeleteStep > 0} onOpenChange={() => cancelHardDelete()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {hardDeleteStep === 1 && "Warning: Permanent Deletion"}
              {hardDeleteStep === 2 && "Second Warning: This Cannot Be Undone"}
              {hardDeleteStep === 3 && "Final Confirmation Required"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {/* Progress indicator */}
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`w-3 h-3 rounded-full ${
                    step <= hardDeleteStep ? 'bg-red-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>

            {hardDeleteStep === 1 && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 font-medium">
                    You are about to permanently delete {hardDeleteTarget === 'tenant' ? 'the entire tenant' : 'a user'}:
                  </p>
                  <p className="text-red-600 font-bold text-lg mt-2">
                    {hardDeleteTarget === 'tenant' ? tenant?.company_name : hardDeleteUser?.full_name}
                  </p>
                </div>
                {hardDeleteTarget === 'tenant' && tenantDetails?.stats && (
                  <div className="bg-gray-50 border rounded-lg p-4">
                    <p className="text-sm text-gray-600 font-medium">This will delete:</p>
                    <ul className="mt-2 space-y-1 text-sm text-gray-700">
                      <li>• {tenantDetails.stats.total_quotations} quotations</li>
                      <li>• {tenantDetails.stats.total_users} users</li>
                      <li>• {tenantDetails.stats.total_b2b_agents} B2B agents</li>
                      <li>• All agency settings and data</li>
                    </ul>
                  </div>
                )}
              </div>
            )}

            {hardDeleteStep === 2 && (
              <div className="space-y-4">
                <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                  <p className="text-red-800 font-bold text-center">
                    ⚠️ THIS ACTION CANNOT BE UNDONE ⚠️
                  </p>
                </div>
                <p className="text-gray-700">
                  Once deleted, all data will be permanently removed from the database. 
                  There is no way to recover this information.
                </p>
                <p className="text-gray-700 font-medium">
                  Are you absolutely sure you want to proceed?
                </p>
              </div>
            )}

            {hardDeleteStep === 3 && (
              <div className="space-y-4">
                <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                  <p className="text-red-800 font-bold text-center">
                    FINAL STEP
                  </p>
                </div>
                <p className="text-gray-700">
                  To confirm permanent deletion, please type the name exactly:
                </p>
                <p className="font-mono bg-gray-100 px-3 py-2 rounded text-center font-bold">
                  {hardDeleteTarget === 'tenant' ? tenant?.company_name : hardDeleteUser?.full_name}
                </p>
                <Input
                  value={hardDeleteConfirmText}
                  onChange={(e) => setHardDeleteConfirmText(e.target.value)}
                  placeholder="Type the name to confirm"
                  className="border-red-300 focus:border-red-500"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelHardDelete}>
              Cancel
            </Button>
            {hardDeleteStep < 3 ? (
              <Button 
                className="bg-red-600 hover:bg-red-700" 
                onClick={proceedHardDelete}
              >
                I Understand, Continue
              </Button>
            ) : (
              <Button 
                className="bg-red-600 hover:bg-red-700" 
                onClick={executeHardDelete}
                disabled={hardDeleteConfirmText !== (hardDeleteTarget === 'tenant' ? tenant?.company_name : hardDeleteUser?.full_name)}
              >
                Permanently Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Email Dialog */}
      <Dialog open={showOnboardingEmailDialog} onOpenChange={setShowOnboardingEmailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Onboarding Email - {onboardingEmailData?.tenant?.company_name}
            </DialogTitle>
            <DialogDescription>
              Copy the HTML below to send as an onboarding email to the tenant.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto border rounded-lg bg-gray-50">
            {onboardingEmailData && (
              <iframe
                srcDoc={generateOnboardingEmailHTML()}
                title="Email Preview"
                className="w-full h-[500px] border-0"
                sandbox="allow-same-origin"
              />
            )}
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowOnboardingEmailDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={copyOnboardingEmail}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {emailCopied ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Copy Email HTML
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Main Dashboard ====================
const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(() => {
    const stored = localStorage.getItem('super_admin');
    return stored ? JSON.parse(stored) : null;
  });

  const handleLogout = () => {
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin');
    setAdmin(null);
    toast.success('Logged out successfully');
  };

  if (!admin) {
    return <SuperAdminLogin onLogin={setAdmin} />;
  }

  const isCMSRoute = location.pathname.startsWith('/admin/cms');

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0A1F44] to-[#162E5E] rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-sm text-[#0A1F44]">Platform Admin</div>
              <div className="text-xs text-gray-500 truncate max-w-[140px]">{admin.email}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <Link 
            to="/admin" 
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/admin/tenants" 
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname.startsWith('/admin/tenants') ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
          >
            <Building2 className="w-5 h-5" />
            Tenants
          </Link>
          <Link 
            to="/admin/subscriptions" 
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname.startsWith('/admin/subscriptions') ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
          >
            <CreditCard className="w-5 h-5" />
            Subscriptions
          </Link>
          <Link 
            to="/admin/activity-log" 
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/activity-log' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
          >
            <History className="w-5 h-5" />
            Activity Log
          </Link>
          <Link 
            to="/admin/templates" 
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname.startsWith('/admin/templates') ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
          >
            <Layout className="w-5 h-5" />
            Quotation Templates
          </Link>

          {/* CMS Section */}
          <div className="pt-4 mt-4 border-t border-gray-200">
            <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Website CMS
            </div>
            <Link 
              to="/admin/cms" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <Globe className="w-5 h-5" />
              Site Settings
            </Link>
            <Link 
              to="/admin/cms/hero" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/hero' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <Palette className="w-5 h-5" />
              Hero Section
            </Link>
            <Link 
              to="/admin/cms/features" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/features' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <FileText className="w-5 h-5" />
              Features
            </Link>
            <Link 
              to="/admin/cms/faqs" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/faqs' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <HelpCircle className="w-5 h-5" />
              FAQs
            </Link>
            <Link 
              to="/admin/cms/testimonials" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/testimonials' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <Quote className="w-5 h-5" />
              Testimonials
            </Link>
            <Link 
              to="/admin/cms/how-it-works" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/how-it-works' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <ListOrdered className="w-5 h-5" />
              How it Works
            </Link>
            <Link 
              to="/admin/cms/videos" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/videos' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <Film className="w-5 h-5" />
              Videos
            </Link>
            <Link 
              to="/admin/cms/cta" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/cta' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <MousePointer className="w-5 h-5" />
              Call to Action
            </Link>
            <Link 
              to="/admin/cms/media" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/media' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <Image className="w-5 h-5" />
              Media Library
            </Link>
            <Link 
              to="/admin/cms/contact" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/admin/cms/contact' ? 'bg-[#0A1F44] text-white' : 'hover:bg-gray-100 text-[#0A1F44]'}`}
            >
              <Mail className="w-5 h-5" />
              Contact Submissions
            </Link>
          </div>
        </nav>

        <div className="p-4 border-t border-gray-200">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-[#0A1F44] hover:text-[#0A1F44] hover:bg-gray-100"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<DashboardOverview />} />
          <Route path="/tenants" element={<TenantList />} />
          <Route path="/tenants/:tenantId" element={<TenantDetail />} />
          <Route path="/activity-log" element={<ActivityLog />} />
          <Route path="/subscriptions" element={<SubscriptionManagement />} />
          <Route path="/templates/*" element={<TemplateManagement />} />
          <Route path="/cms/*" element={<CMSEditor />} />
        </Routes>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
